import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';

export interface U2000InfoTooltipProps {
  message: string;
  className?: string;
  iconClassName?: string;
  align?: 'center' | 'left' | 'right' | 'auto';
  /** Icône affichée : info (défaut) ou point d'interrogation. */
  icon?: 'info' | 'question';
}

type TooltipAlign = 'center' | 'left' | 'right';

interface TooltipViewportPosition {
  top: number;
  left: number;
}

const TOOLTIP_GAP_PX = 8;
const VIEWPORT_PAD_PX = 8;
const TOOLTIP_Z_INDEX = 60;

function resolveAutoAlign(anchor: DOMRect): TooltipAlign {
  const vw = window.innerWidth;
  if (anchor.left < 120) {
    return 'left';
  }
  if (anchor.right > vw - 120) {
    return 'right';
  }
  return 'center';
}

function computeTooltipViewportPosition(
  anchor: DOMRect,
  tooltipWidth: number,
  tooltipHeight: number,
  align: TooltipAlign,
): TooltipViewportPosition {
  let top = anchor.bottom + TOOLTIP_GAP_PX;
  if (top + tooltipHeight > window.innerHeight - VIEWPORT_PAD_PX) {
    top = anchor.top - tooltipHeight - TOOLTIP_GAP_PX;
  }
  top = Math.max(VIEWPORT_PAD_PX, top);

  let left: number;
  if (align === 'left') {
    left = anchor.left;
  } else if (align === 'right') {
    left = anchor.right - tooltipWidth;
  } else {
    left = anchor.left + anchor.width / 2 - tooltipWidth / 2;
  }

  const maxLeft = window.innerWidth - VIEWPORT_PAD_PX - tooltipWidth;
  left = Math.max(VIEWPORT_PAD_PX, Math.min(left, maxLeft));

  return { top, left };
}

const U2000InfoTooltip: React.FC<U2000InfoTooltipProps> = ({
  message,
  className = '',
  iconClassName = 'h-3.5 w-3.5 text-blue-500 hover:text-blue-600',
  align = 'auto',
  icon = 'info',
}) => {
  const [show, setShow] = useState(false);
  const [viewportPosition, setViewportPosition] = useState<TooltipViewportPosition | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!show || !anchorRef.current) {
      setViewportPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!anchorRef.current) {
        return;
      }
      const anchor = anchorRef.current.getBoundingClientRect();
      const tooltipEl = tooltipRef.current;
      const tooltipWidth = tooltipEl?.offsetWidth ?? 288;
      const tooltipHeight = tooltipEl?.offsetHeight ?? 48;
      const effectiveAlign: TooltipAlign =
        align === 'auto' ? resolveAutoAlign(anchor) : align;
      setViewportPosition(
        computeTooltipViewportPosition(anchor, tooltipWidth, tooltipHeight, effectiveAlign),
      );
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [show, align, message]);

  const tooltipClass =
    'fixed w-max min-w-[18rem] max-w-md rounded-md bg-gray-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg whitespace-normal wrap-break-word pointer-events-none';

  const tooltipNode =
    show && typeof document !== 'undefined' ? (
      <span
        ref={tooltipRef}
        role="tooltip"
        className={tooltipClass}
        style={{
          top: viewportPosition?.top ?? -9999,
          left: viewportPosition?.left ?? -9999,
          zIndex: TOOLTIP_Z_INDEX,
          visibility: viewportPosition ? 'visible' : 'hidden',
        }}
      >
        {message}
      </span>
    ) : null;

  return (
    <>
      <span
        ref={anchorRef}
        className={`inline-flex cursor-help items-center ${className}`}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => {
          // N'ouvre sur focus que pour la navigation clavier (:focus-visible),
          // pas lors d'un focus programmatique (auto-focus dialogue) ou souris.
          if (anchorRef.current?.matches(':focus-visible')) {
            setShow(true);
          }
        }}
        onBlur={() => setShow(false)}
        tabIndex={0}
        role="button"
        aria-label={message}
      >
        {icon === 'question' ? (
          <FaQuestionCircle className={iconClassName} aria-hidden />
        ) : (
          <FaInfoCircle className={iconClassName} aria-hidden />
        )}
      </span>
      {tooltipNode && createPortal(tooltipNode, document.body)}
    </>
  );
};

export default U2000InfoTooltip;
